<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Partners" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search partners..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
          <RouterLink to="/partners/create" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Add New Partner</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">Partner ID</th>
                  <th scope="col">Logo</th>
                  <th scope="col">Name</th>
                  <th scope="col">MoU Link</th>
                  <th scope="col">Status</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="5" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredPartners.length === 0">
                  <td colspan="5" class="text-center py-4">No partners found.</td>
                </tr>
                <tr v-else v-for="p in filteredPartners" :key="p.id">
                  <td>#{{ p.id }}</td>
                  <td>
                    <img :src="getLogoUrl(p.logo)" alt="logo" class="rounded-3 border" style="max-height: 40px; max-width: 120px; object-fit: contain; padding: 2px; background-color: #fafafa;" />
                  </td>
                  <td class="fw-medium text-dark">{{ p.name }}</td>
                  <td>
                    <a v-if="p.mou_link" :href="p.mou_link" target="_blank" class="text-primary text-decoration-none hover-text-primary fs-13">
                      <i class="ri-external-link-line me-1"></i>View MoU
                    </a>
                    <span v-else class="text-muted fs-13">-</span>
                  </td>
                  <td>
                    <span class="badge p-2 fs-12 fw-normal" :class="p.is_active ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'">
                      {{ p.is_active ? 'Active' : 'Inactive' }}
                    </span>
                  </td>
                  <td>
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/partners/${p.id}/edit`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-body">edit</i>
                      </RouterLink>
                      <button @click="deletePartner(p.id)" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-danger">delete</i>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import partnerService from '@/services/partnerService';

export default defineComponent({
  name: 'PartnerListPage',
  components: {
    PageTitle
  },
  setup() {
    const partners = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const fetchPartners = async () => {
      loading.value = true;
      try {
        const response = await partnerService.getAll();
        partners.value = response.data;
      } catch (error) {
        console.error('Error fetching partners:', error);
      } finally {
        loading.value = false;
      }
    };

    const getLogoUrl = (logo: string) => {
      if (!logo) return '';
      if (logo.startsWith('http')) return logo;
      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://backend-beta.rayacheck.id'
        : 'http://jurnal-backend.test';
      return `${baseUrl}/storage/${logo}`;
    };

    const deletePartner = async (id: number) => {
      if (confirm('Are you sure you want to delete this partner?')) {
        try {
          await partnerService.delete(id);
          fetchPartners();
        } catch (error) {
          console.error('Error deleting partner:', error);
        }
      }
    };

    const filteredPartners = computed(() => {
      if (!searchQuery.value) return partners.value;
      const q = searchQuery.value.toLowerCase();
      return partners.value.filter(p => p.name.toLowerCase().includes(q));
    });

    onMounted(() => {
      fetchPartners();
    });

    return {
      partners,
      loading,
      searchQuery,
      filteredPartners,
      getLogoUrl,
      deletePartner
    };
  }
});
</script>
